/**
 * The reads Customer 360's header, Overview and Change drawer share (D19 phase 21b).
 *
 * Every one of them is a read the desk already makes elsewhere on this page, with the SAME query key and the same
 * parameters, so the header and the tab that shows the full figure are one cache entry and cannot disagree:
 *   · customer_calendar     — CustomerCalendar (Subscription & calendar)
 *   · customer_balances     — CustomerOutstanding (Bills & payments)
 *   · customer_month_bill   — CustomerOutstanding
 *   · customer_delivery_facts — DeliveryFacts (Overview › Profile and health)
 *   · customer_complaints   — CustomerComplaints (Tickets)
 *   · customer_timeline     — CustomerActivity (History)
 *   · product_variants      — CustomerCalendar's variant picker
 *   · delivery_cutoff_state — Delivery › Plan and Cutoff
 *   · customer_phone_reports — CustomerDeliveries (Orders & deliveries › Deliveries), migration 219
 * Nothing here is new data and nothing is invented: a read that is refused or empty leaves its fact out.
 */
import { useQuery } from '@tanstack/react-query';
import { rpc, supabase } from '../../lib/supabase';

export interface Qty { variant: string; quantity: number }
export interface CalendarDay {
  date: string; planned: Qty[]; order: { code: string; status: string; source: string; lines: Qty[] } | null; extraOrders: number;
  delivered: { given: number; ordered: number; outcome: string } | null;
  changes: { effect: string; variant: string; from: string; until: string | null; quantity: number | null; reason: string | null }[]; locked: boolean;
}
export interface CalendarRead { from: string; to: string; today: string; days: CalendarDay[] }
export interface BalanceRead { balance_paise: number; charged_paise: number; paid_paise: number; last_paid_on: string | null; delivery_days_since_paid: number; last_delivered: string | null; customer_id: string }
/** api.customer_outstanding (Finance phase 1, plan 4.8): the one balance, with where it comes from. All integer paise. */
export interface OutstandingRead {
  customerId: string; asOf: string; balancePaise: number; billedPaise: number; unbilledPaise: number; source: 'LEDGER' | 'COMPUTED';
  lastPaidOn: string | null; ledgerPostedThrough: string | null; billingStarts: string;
}
/**
 * What Customer 360 shows as Outstanding: the Finance figure (`balance_paise` is kept as an alias of `balancePaise` so every
 * caller compiles) plus billed, this month so far and the source. The all-time totals and the unpaid-days count are not in
 * the Finance figure; they come from customer_balances, whose balance_paise is the same definition, and are never added to it.
 */
export interface BalanceView extends Omit<BalanceRead, 'balance_paise' | 'charged_paise' | 'paid_paise' | 'delivery_days_since_paid' | 'last_delivered'> {
  balance_paise: number; billedPaise: number; unbilledPaise: number; source: 'LEDGER' | 'COMPUTED'; billingStarts: string; asOf: string;
  ledgerPostedThrough: string | null;
  charged_paise: number | null; paid_paise: number | null; delivery_days_since_paid: number | null; last_delivered: string | null;
}
export interface MonthBillRead { month: string; opening: number; paid: number; closing: number; charged: number; unpriced: boolean; byVariant: { variant: string; units: number; amount: number; days: number }[] }
export interface DeliveryFactsRead {
  route?: { code: string; name: string; usualRider?: string; usualRiderCode?: string };
  pin?: { status: 'ok' | 'none'; source?: string; accuracyM?: number; setAt?: string; pendingApproval: boolean };
  since?: string;
  lastDelivered?: { date: string; at?: string; round: string };
  nextPlanned?: { date: string; status: string; units: number };
  bottlesOwed?: number; oldestOwedDays?: number; openExceptions?: number;
}
export interface ComplaintRead { complaint_id: string; received_at: string; status: string; decided_at: string | null; subject: string | null; description: string }
export interface TimelineRead { at: string | null; kind: string; what: string; detail: string | null }
export interface CutoffStateRead { time: string; configured: boolean; tomorrowCutoffAt: string; runs?: { serviceDate: string }[] }

/** Today in India, as the calendar and the payments tab compute it. */
import { istToday } from '../../lib/businessDay';
export { istToday };
export const addDays = (d: string, n: number) => new Date(new Date(`${d}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);
export const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
export const monthEnd = (d: string) => { const x = new Date(`${monthStart(d)}T00:00:00Z`); x.setUTCMonth(x.getUTCMonth() + 1); x.setUTCDate(0); return x.toISOString().slice(0, 10); };
export const nextMonth = (d: string) => { const x = new Date(`${monthStart(d)}T00:00:00Z`); x.setUTCMonth(x.getUTCMonth() + 1); return x.toISOString().slice(0, 10); };
export const inr = (paise: number | null | undefined) => `₹ ${((paise ?? 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const shortDay = (d: string | null | undefined) => (d ? new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : '—');
export const linesLabel = (q: readonly Qty[]) => (q.length ? q.map((x) => `${x.quantity} × ${x.variant}`).join(' · ') : 'nothing');
export const unitsOf = (q: readonly Qty[]) => q.reduce((n, x) => n + x.quantity, 0);
/** What a day brings now: the order once there is one, else what the agreement plans. */
export const dayLines = (d: CalendarDay): Qty[] => (d.order && d.order.status !== 'CANCELLED' ? d.order.lines : d.planned);

/** A calendar month, exactly as CustomerCalendar reads it (same key, same range). */
export function useCalendarMonth(customerId: string, month: string, enabled = true, door: string | null = null) {
  return useQuery({
    // 140900: one address's calendar when a door is named (the key keeps the whole customer's as it was)
    queryKey: door ? ['customer-calendar', customerId, month, door] : ['customer-calendar', customerId, month],
    enabled,
    queryFn: () => rpc<CalendarRead>('customer_calendar', { p_customer_id: customerId, p_from: month, p_to: monthEnd(month), ...(door ? { p_door: door } : {}) }),
  });
}

/** The customer's addresses that have a plan (140500/140800): two or more means the pages choose an address. */
export interface PlannedDoor { locationId: string; code: string; label: string | null; isPrimary: boolean; status: string; route: string | null; hasPlan: boolean; subscriptionId: string | null }
export function usePlannedDoors(customerId: string) {
  const q = useQuery({ queryKey: ['door-routes', customerId], retry: false, queryFn: () => rpc<PlannedDoor[]>('door_routes', { p_customer_id: customerId }) });
  return (q.data ?? []).filter((x) => x.hasPlan && x.status === 'ACTIVE');
}

/** Any range of the same calendar read (the Change drawer's dates). */
export function useCalendarRange(customerId: string, from: string | null, to: string | null) {
  return useQuery({
    queryKey: ['customer-calendar', customerId, 'range', from, to],
    enabled: Boolean(from && to),
    queryFn: () => rpc<CalendarRead>('customer_calendar', { p_customer_id: customerId, p_from: from, p_to: to }),
  });
}

export function useCustomerBalance(customerId: string) {
  return useQuery({
    queryKey: ['customer-balance', customerId],
    queryFn: async (): Promise<BalanceView> => {
      const [o, rows] = await Promise.all([
        rpc<OutstandingRead>('customer_outstanding', { p_customer_id: customerId }),
        rpc<BalanceRead[]>('customer_balances', { p_as_of: null, p_customer_id: customerId }),   // 137600: one customer
      ]);
      const aux = rows.find((b) => b.customer_id === customerId) ?? null;
      return {
        customer_id: customerId, balance_paise: o.balancePaise, billedPaise: o.billedPaise, unbilledPaise: o.unbilledPaise, source: o.source,
        billingStarts: o.billingStarts, asOf: o.asOf, ledgerPostedThrough: o.ledgerPostedThrough, last_paid_on: o.lastPaidOn ?? aux?.last_paid_on ?? null,
        charged_paise: aux?.charged_paise ?? null, paid_paise: aux?.paid_paise ?? null,
        delivery_days_since_paid: aux?.delivery_days_since_paid ?? null, last_delivered: aux?.last_delivered ?? null,
      };
    },
  });
}

/** "billed ₹A + this month so far ₹B": the two parts Finance returns; the sum is Finance's, not worked out here. */
export const outstandingSplit = (b: { billedPaise: number; unbilledPaise: number }) => `billed ${inr(b.billedPaise)} + this month so far ${inr(b.unbilledPaise)}`;
/** The deep link from Customer 360 into the Finance ledger check (plan T5). */
export const ledgerCheckPath = (code: string) => `/finance/ledger-check?customer=${encodeURIComponent(code)}`;

/** The words that say where an Outstanding figure came from (Finance phase 1, A12). */
export const balanceSourceLine = (source: 'LEDGER' | 'COMPUTED') =>
  source === 'LEDGER' ? 'from the Finance ledger' : 'worked out from deliveries and payments';

export function useMonthBill(customerId: string, month: string) {
  return useQuery({ queryKey: ['customer-bill', customerId, month], queryFn: () => rpc<MonthBillRead>('customer_month_bill', { p_customer_id: customerId, p_month: `${month}-01` }) });
}

export function useDeliveryFacts(customerId: string) {
  return useQuery({ queryKey: ['customer-delivery-facts', customerId], queryFn: () => rpc<DeliveryFactsRead>('customer_delivery_facts', { p_customer_id: customerId }) });
}

export function useComplaints(customerId: string) {
  return useQuery({
    queryKey: ['customer-complaints', customerId],
    queryFn: () => rpc<ComplaintRead[]>('customer_complaints', { p_from: addDays(istToday(), -365), p_status: null, p_customer_id: customerId }),
  });
}

export function useCustomerTimeline(customerId: string) {
  return useQuery({ queryKey: ['customer-timeline', customerId], queryFn: () => rpc<TimelineRead[]>('customer_timeline', { p_customer_id: customerId, p_limit: 300 }) });
}

export function useVariantCodes() {
  return useQuery({
    queryKey: ['variant-codes'], staleTime: 600_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('product_variants').select('code,name,status').order('code');
      if (error) throw error;
      return (data ?? []).filter((v: { status: string }) => v.status === 'ACTIVE') as { code: string; name: string }[];
    },
  });
}

/** The cut-off, as Delivery › Plan and Cutoff reads it. Refused for a viewer without delivery access: the caller then states the default. */
export function useCutoffState() {
  return useQuery({ queryKey: ['delivery-cutoff-state'], refetchInterval: 60_000, retry: false, queryFn: () => rpc<CutoffStateRead>('delivery_cutoff_state', { p_days: 14 }) });
}

/** What the rider's phone reported for a day of this customer, whether or not the ERP has booked it yet (migration 219).
 *  A hand-over held because the round carried no stock stays PENDING on the line; this is the rider's word beside it. */
export interface PhoneDay { date: string; units: number; event: string; outcome: string; heldCode: string | null; heldDetail: string | null; at: string }
export function usePhoneDays(customerId: string) {
  return useQuery({
    queryKey: ['customer-phone-reports', customerId],
    queryFn: () => rpc<{ service_date: string; trip_code: string; report: { event: string; at: string; outcome: string; heldCode: string | null; heldDetail: string | null; units: number } | null }[]>(
      'customer_phone_reports', { p_customer_id: customerId, p_days: 37 }),
    select: (rows) => {
      const byDate = new Map<string, PhoneDay>();
      for (const r of rows) {
        if (!r.report) continue;
        const d = r.service_date.slice(0, 10);
        const prev = byDate.get(d);
        byDate.set(d, { date: d, units: (prev?.units ?? 0) + (r.report.event === 'delivery_completed' ? r.report.units : 0), event: r.report.event,
                        outcome: prev?.outcome === 'HELD' ? 'HELD' : r.report.outcome, heldCode: r.report.heldCode ?? prev?.heldCode ?? null,
                        heldDetail: r.report.heldDetail ?? prev?.heldDetail ?? null, at: r.report.at });
      }
      return byDate;
    },
  });
}
/** Units the rider reported as handed over that the ERP has not booked yet (held). */
export const waitingUnits = (p: PhoneDay | undefined, booked: number) =>
  p && p.event === 'delivery_completed' && p.outcome === 'HELD' ? Math.max(p.units - booked, 0) : 0;
export const heldShort = (code: string | null) =>
  code === 'HELD_NOT_LOADED' ? 'waits for stock on the round' : code ? 'waits at the ERP' : '';
